/**
 * Utilities for building and downloading the "errors CSV" in the work-pattern
 * import flow (#414).
 *
 * The errors CSV is the subset of the original uploaded file that contained
 * blocking validation failures, with a trailing `Reason` column appended so
 * consultants can fix values in Excel and re-upload. The extra column is an
 * unmapped column the import ignores on the next upload.
 *
 * The downloadable issues CSV includes blocking error rows and warning rows
 * (whatever the caller places in `errorReasonsByCsvRow` — see
 * `WorkPatternImportInput`). Passed rows are never included.
 *
 * When `patterns` and `patternCsvRowNumbers` are supplied, the data cells of
 * each pattern-backed row are rebuilt from the current in-memory
 * `WorkPatternRow` (reflecting any Edit in UI changes) rather than copied
 * verbatim from the original upload text. Parse-failed rows (no backing
 * pattern) always use the original line.
 *
 * Note: the CSV-injection guard and cell-escaping logic below are intentionally
 * duplicated from `pay-component-import/utils/resultsExport.ts` to avoid
 * cross-module imports (feature-module boundaries). If this logic is promoted
 * to `src/shared` in future, remove the duplication.
 */
import type { WorkPatternRow } from "../../../generated/core-api";
import {
  mergeWorkPatternRowIntoCsvCells,
  resolveHeaderIndicesFromCsvHeader,
} from "../../hooks/WorkPatternImport/useWorkPatternImportCsvParser";

/**
 * Neutralises spreadsheet formula (CSV) injection: a cell whose first character
 * is `= + - @` (or a leading tab / carriage return) is interpreted as a formula
 * by Excel / Google Sheets, which can exfiltrate data or run commands. Prefix
 * such values with an apostrophe so they render as literal text.
 *
 * See OWASP "CSV Injection".
 */
function neutraliseFormula(value: string): string {
  return /^[=+\-@\t\r]/.test(value) ? `'${value}` : value;
}

/**
 * Wraps a CSV cell value in double quotes if it contains commas, double
 * quotes, or line breaks — otherwise returns the value as-is.
 * Formula-trigger characters are neutralised first.
 */
function escapeCsvCell(value: string): string {
  const safe = neutraliseFormula(value);
  if (/[",\n\r]/.test(safe)) {
    return `"${safe.replace(/"/g, '""')}"`;
  }
  return safe;
}

/**
 * Splits a single CSV line into its constituent cell values, honouring
 * double-quoted fields (including `""` escape sequences for embedded quotes).
 * Multi-line quoted fields (embedded newlines) are NOT supported — the file
 * is split into lines before this is called.
 */
function splitCsvCells(line: string): string[] {
  const cells: string[] = [];
  let i = 0;
  while (i <= line.length) {
    if (line[i] === '"') {
      // Quoted field — consume until the closing unescaped quote.
      let cell = "";
      i++; // skip opening quote
      while (i < line.length) {
        if (line[i] === '"') {
          if (line[i + 1] === '"') {
            cell += '"';
            i += 2;
          } else {
            i++; // skip closing quote
            break;
          }
        } else {
          cell += line[i++];
        }
      }
      cells.push(cell);
      if (line[i] === ",") i++; // skip field delimiter
    } else {
      // Unquoted field — up to the next comma or end of string.
      const end = line.indexOf(",", i);
      if (end === -1) {
        cells.push(line.slice(i));
        break;
      } else {
        cells.push(line.slice(i, end));
        i = end + 1;
      }
    }
  }
  return cells;
}

/**
 * Parses a raw CSV source line into cells, neutralises any formula-injection
 * triggers in each cell, then reassembles the line so it is safe to include
 * verbatim in the errors download without becoming a spreadsheet formula.
 */
function escapeSourceLine(line: string): string {
  return splitCsvCells(line).map(escapeCsvCell).join(",");
}

/**
 * Builds an errors CSV from the original uploaded file text.
 *
 * Mirrors the original column layout exactly, appending a trailing `Reason`
 * column that joins all error messages for that row with "; ". Passing rows
 * (and warning-only rows) are omitted so the output is re-uploadable.
 *
 * Row-number alignment: the parser does `text.trim().split("\n")` where
 * `lines[0]` is the header (row 1) and `lines[i]` (i ≥ 1) is csvRowNumber
 * `i + 1`. This function mirrors the same split so csvRowNumbers always match
 * array indices.
 *
 * When `patterns` and `patternCsvRowNumbers` are supplied, the data cells for
 * each pattern-backed error row are rebuilt from the current in-memory pattern
 * (reflecting any Edit in UI edits) instead of the stale original text. Parse-
 * failed rows with no backing pattern always use the original line verbatim.
 *
 * @param rawCsvText  The original file text as returned by `file.text()`.
 * @param reasonsByCsvRow  Map of csvRowNumber (≥ 2) → array of error messages.
 *   Only rows present in the map are included in the output.
 * @param patterns  Optional current in-memory patterns (pending edits applied).
 * @param patternCsvRowNumbers  Parallel array: patternCsvRowNumbers[i] is the
 *   1-based CSV row number that patterns[i] was parsed from.
 */
export function buildWorkPatternErrorsCsv(
  rawCsvText: string,
  reasonsByCsvRow: Map<number, string[]>,
  patterns?: WorkPatternRow[],
  patternCsvRowNumbers?: number[]
): string {
  if (!rawCsvText || reasonsByCsvRow.size === 0) return "";

  const normalised = rawCsvText.replace(/\r\n/g, "\n").replace(/\r/g, "\n");
  const lines = normalised.trim().split("\n");

  if (lines.length < 2) return "";

  // Resolve header indices once (needed to merge pattern values).
  const headerIndices =
    patterns && patternCsvRowNumbers
      ? resolveHeaderIndicesFromCsvHeader(lines[0])
      : null;

  // Build a lookup: csvRowNumber → pattern index, for fast O(1) access.
  const rowToPatternIndex = new Map<number, number>();
  if (patternCsvRowNumbers) {
    patternCsvRowNumbers.forEach((csvRow, idx) =>
      rowToPatternIndex.set(csvRow, idx)
    );
  }

  const out: string[] = [`${escapeSourceLine(lines[0])},Reason`];

  for (let i = 1; i < lines.length; i++) {
    const csvRowNumber = i + 1;
    const reasons = reasonsByCsvRow.get(csvRowNumber);
    if (reasons && reasons.length > 0) {
      let dataCells: string;

      const patternIdx = rowToPatternIndex.get(csvRowNumber);
      if (
        headerIndices !== null &&
        patterns !== undefined &&
        patternIdx !== undefined
      ) {
        // Merge in-memory pattern values on top of the original cells.
        const originalCells = splitCsvCells(lines[i]);
        const merged = mergeWorkPatternRowIntoCsvCells(
          originalCells,
          headerIndices,
          patterns[patternIdx]
        );
        dataCells = merged.map(escapeCsvCell).join(",");
      } else {
        // Parse-failed row or no patterns supplied — use the original text.
        dataCells = escapeSourceLine(lines[i]);
      }

      out.push(`${dataCells},${escapeCsvCell(reasons.join("; "))}`);
    }
  }

  // Only emit anything if we actually found matching failed rows.
  return out.length > 1 ? out.join("\n") : "";
}

/**
 * Triggers a browser download of `content` as a CSV file without a server
 * round-trip. The object URL is revoked shortly after the download starts.
 */
function triggerDownload(content: string, filename: string): void {
  const blob = new Blob([content], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);

  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  anchor.style.display = "none";
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);

  setTimeout(() => {
    URL.revokeObjectURL(url);
  }, 100);
}

/**
 * Builds the errors CSV and triggers a browser download.
 *
 * @param rawCsvText           The original uploaded file text.
 * @param reasonsByCsvRow      Map of csvRowNumber → error messages.
 * @param filename             Default: `"work-pattern-import-errors.csv"`.
 * @param patterns             Optional current in-memory patterns (pending
 *   edits applied); forwarded to `buildWorkPatternErrorsCsv`.
 * @param patternCsvRowNumbers Parallel array mapping pattern index → CSV row.
 */
export function downloadWorkPatternErrorsCsv(
  rawCsvText: string,
  reasonsByCsvRow: Map<number, string[]>,
  filename = "work-pattern-import-errors.csv",
  patterns?: WorkPatternRow[],
  patternCsvRowNumbers?: number[]
): void {
  const csv = buildWorkPatternErrorsCsv(
    rawCsvText,
    reasonsByCsvRow,
    patterns,
    patternCsvRowNumbers
  );
  if (!csv) return;
  triggerDownload(csv, filename);
}
